import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { useAppStore } from './appStore';
import { EVENTS, track, trackSignup } from '../lib/analytics';
import { rememberedVisit } from '../lib/signupSource';

const API = API_URL;

/**
 * A new account: count it, and save on it where its owner came from (see
 * lib/signupSource). Best effort: a failure never gets in the way of signing
 * up, and the API accepts it once, from a new account only.
 */
const recordSignup = (method, sessionToken) => {
  const fromDemo = trackSignup(method);
  axios.post(
    `${API}/auth/signup-source`,
    { ...(rememberedVisit() || {}), from_demo: fromDemo },
    { headers: { Authorization: `Bearer ${sessionToken}` }, withCredentials: true },
  ).catch(() => {});
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      sessionToken: null,
      isLoading: true,
      isAuthenticated: false,
      isDemo: false,
      demoExpiresAt: null,
      // A demo whose hour ran out while the tab was closed: the next page
      // load finds the session gone, and AuthProvider shows /demo-ended
      demoLapsed: false,
      isAdmin: false, // Cached admin status
      adminStatusChecked: false, // Track if we've already checked
      hasProjectsPlan: false, // Cached Projects plan status
      projectsPlanChecked: false, // Track if we've checked
      // Subscription state
      subscription: null, // { plan, status, trial_ends_at, days_remaining, is_trial, can_create_content }
      subscriptionChecked: false,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setSessionToken: (token) => set({ sessionToken: token }),

      startDemo: async () => {
        try {
          // CRITICAL: Clear previous user's data before setting new session
          useAppStore.getState().clearAllData();
          
          const response = await axios.post(`${API}/demo/start`, {}, { withCredentials: true });
          const token = response.data.session_token;
          localStorage.setItem('session_token', token);
          set({ 
            user: response.data.user, 
            sessionToken: token,
            isAuthenticated: true,
            isLoading: false,
            isDemo: true,
            demoExpiresAt: response.data.expires_at,
            demoLapsed: false
          });
          track(EVENTS.DEMO_STARTED);
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: error.response?.data?.detail || 'Failed to start demo' };
        }
      },

      checkDemoStatus: async () => {
        const { sessionToken } = get();
        if (!sessionToken) return { isDemo: false };
        
        try {
          const response = await axios.get(`${API}/demo/status`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          set({ 
            isDemo: response.data.is_demo,
            demoExpiresAt: response.data.expires_at || null
          });
          return response.data;
        } catch (error) {
          return { isDemo: false };
        }
      },

      // Subscription functions
      checkSubscriptionStatus: async (forceRefresh = false) => {
        const { sessionToken, subscriptionChecked, subscription } = get();
        
        // Return cached result if already checked (unless force refresh)
        if (subscriptionChecked && !forceRefresh && subscription) {
          return { success: true, subscription };
        }
        
        if (!sessionToken) return { success: false, subscription: null };
        
        try {
          const response = await axios.get(`${API}/subscription/status`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          set({ subscription: response.data, subscriptionChecked: true });
          return { success: true, subscription: response.data };
        } catch (error) {
          set({ subscriptionChecked: true });
          return { success: false, error: error.response?.data?.detail || 'Failed to get subscription status' };
        }
      },

      getSubscriptionPlans: async () => {
        try {
          const response = await axios.get(`${API}/subscription/plans`, { withCredentials: true });
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to get subscription plans' };
        }
      },

      createCheckoutSession: async (plan) => {
        const { sessionToken } = get();
        try {
          const response = await axios.post(`${API}/subscription/checkout`, {
            plan,
            origin_url: window.location.origin
          }, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to create checkout session' };
        }
      },

      createOnboardingCheckout: async () => {
        const { sessionToken } = get();
        try {
          const response = await axios.post(`${API}/subscription/onboarding/checkout`, {
            origin_url: window.location.origin
          }, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to start onboarding checkout' };
        }
      },

      checkCheckoutStatus: async (sessionId) => {
        const { sessionToken } = get();
        try {
          const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          // Refresh subscription status after checking checkout
          if (response.data.payment_status === 'paid') {
            get().checkSubscriptionStatus(true);
          }
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to check checkout status' };
        }
      },

      openBillingPortal: async () => {
        const { sessionToken } = get();
        try {
          const response = await axios.post(`${API}/subscription/portal`, {
            origin_url: window.location.origin
          }, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to open billing portal' };
        }
      },

      resubscribe: async (plan) => {
        const { sessionToken } = get();
        try {
          const response = await axios.post(`${API}/subscription/resubscribe`, {
            plan,
            origin_url: window.location.origin
          }, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          // Refresh subscription status after resubscribing
          get().checkSubscriptionStatus(true);
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to resubscribe' };
        }
      },

      cancelSubscription: async () => {
        const { sessionToken } = get();
        try {
          const response = await axios.post(`${API}/subscription/cancel`, {}, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          // Refresh subscription status
          get().checkSubscriptionStatus(true);
          return { success: true, message: response.data.message };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to cancel subscription' };
        }
      },

      uncancelSubscription: async () => {
        const { sessionToken } = get();
        try {
          const response = await axios.post(`${API}/subscription/uncancel`, {}, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          // Refresh subscription status
          get().checkSubscriptionStatus(true);
          return { success: true, message: response.data.message };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to reactivate subscription' };
        }
      },

      login: async (email, password) => {
        try {
          // CRITICAL: Clear previous user's data before setting new session
          useAppStore.getState().clearAllData();
          
          const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
          const token = response.data.session_token;
          // Store token directly in localStorage for components that need it
          localStorage.setItem('session_token', token);
          set({ 
            user: response.data.user, 
            sessionToken: token,
            isAuthenticated: true,
            isLoading: false,
            subscriptionChecked: false // Reset to check on next auth check
          });
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: error.response?.data?.detail || 'Login failed' };
        }
      },

      register: async (email, name, password) => {
        try {
          // CRITICAL: Clear previous user's data before setting new session
          useAppStore.getState().clearAllData();
          
          const response = await axios.post(`${API}/auth/register`, { email, name, password }, { withCredentials: true });
          const token = response.data.session_token;
          // Store token directly in localStorage for components that need it
          localStorage.setItem('session_token', token);
          set({ 
            user: response.data.user, 
            sessionToken: token,
            isAuthenticated: true,
            isLoading: false,
            subscriptionChecked: false
          });
          recordSignup('email', token);
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: error.response?.data?.detail || 'Registration failed' };
        }
      },

      // Sign in with Google, Apple or Microsoft: the API sends the browser to
      // the provider and back, then to /dashboard#login_code=..., which
      // AuthProvider redeems with processOAuthLogin below.
      startOAuthLogin: (provider) => {
        window.location.href = `${API}/auth/${provider}/start?origin=${encodeURIComponent(window.location.origin)}`;
      },

      processOAuthLogin: async (loginCode) => {
        try {
          // CRITICAL: Clear previous user's data before setting new session
          useAppStore.getState().clearAllData();
          
          const response = await axios.post(`${API}/auth/oauth/exchange`, { code: loginCode }, {
            withCredentials: true
          });
          const token = response.data.session_token;
          // Store token directly in localStorage for components that need it
          localStorage.setItem('session_token', token);
          set({ 
            user: response.data, 
            sessionToken: token,
            isAuthenticated: true,
            isLoading: false,
            subscriptionChecked: false
          });
          if (response.data.new_account) recordSignup(response.data.provider || 'oauth', token);
          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return { success: false, error: error.response?.data?.detail || 'Sign-in failed' };
        }
      },

      checkAuth: async () => {
        let { sessionToken } = get();
        
        // Also check localStorage for legacy token
        if (!sessionToken) {
          sessionToken = localStorage.getItem('session_token');
          if (sessionToken) {
            set({ sessionToken });
          }
        }
        
        if (!sessionToken) {
          set({ isLoading: false, isAuthenticated: false, isDemo: false });
          return;
        }
        
        // Ensure localStorage is in sync
        localStorage.setItem('session_token', sessionToken);
        
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          set({ user: response.data, isLoading: false, isAuthenticated: true });
          
          // Check if this is a demo session
          const demoResponse = await axios.get(`${API}/demo/status`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          set({ 
            isDemo: demoResponse.data.is_demo,
            demoExpiresAt: demoResponse.data.expires_at || null
          });

          // Check subscription status for non-demo users
          if (!demoResponse.data.is_demo) {
            get().checkSubscriptionStatus();
          }
        } catch (error) {
          localStorage.removeItem('session_token');
          // Sticky: two checks can fail at once, and the second sees isDemo already cleared
          const demoLapsed = get().demoLapsed || get().isDemo;
          set({ user: null, sessionToken: null, isLoading: false, isAuthenticated: false, isDemo: false, demoExpiresAt: null, demoLapsed, subscription: null, subscriptionChecked: false });
        }
      },

      logout: async () => {
        const { sessionToken } = get();
        try {
          await axios.post(`${API}/auth/logout`, {}, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
        } catch (error) {
          console.error('Logout error:', error);
        }
        localStorage.removeItem('session_token');
        // CRITICAL: Clear ALL app data to prevent data leakage between users
        useAppStore.getState().clearAllData();
        set({ user: null, sessionToken: null, isAuthenticated: false, isDemo: false, demoExpiresAt: null, isAdmin: false, adminStatusChecked: false, subscription: null, subscriptionChecked: false });
      },

      updateProfile: async (profileData) => {
        const { sessionToken } = get();
        try {
          const response = await axios.put(`${API}/auth/update-profile`, profileData, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          set({ user: response.data });
          return { success: true, user: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to update profile' };
        }
      },

      changePassword: async (currentPassword, newPassword) => {
        const { sessionToken } = get();
        try {
          await axios.put(`${API}/auth/change-password`, {
            current_password: currentPassword,
            new_password: newPassword
          }, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          return { success: true };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to change password' };
        }
      },

      getAccountType: async () => {
        const { sessionToken } = get();
        try {
          const response = await axios.get(`${API}/auth/account-type`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to get account type' };
        }
      },

      // Admin functions - with caching to avoid repeated API calls
      checkAdminStatus: async (forceRefresh = false) => {
        const { sessionToken, adminStatusChecked, isAdmin } = get();
        
        // Return cached result if already checked (unless force refresh)
        if (adminStatusChecked && !forceRefresh) {
          return { success: true, isAdmin };
        }
        
        try {
          const response = await axios.get(`${API}/admin/check`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          const adminStatus = response.data.is_admin;
          set({ isAdmin: adminStatus, adminStatusChecked: true });
          return { success: true, isAdmin: adminStatus };
        } catch (error) {
          set({ isAdmin: false, adminStatusChecked: true });
          return { success: false, isAdmin: false };
        }
      },

      // Projects plan check - with caching
      checkProjectsPlanAccess: async (forceRefresh = false) => {
        const { sessionToken, projectsPlanChecked, hasProjectsPlan } = get();
        
        // Return cached result if already checked
        if (projectsPlanChecked && !forceRefresh) {
          return { success: true, hasAccess: hasProjectsPlan };
        }
        
        try {
          const response = await axios.get(`${API}/projects-plan/check`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          const hasAccess = response.data.has_access;
          set({ hasProjectsPlan: hasAccess, projectsPlanChecked: true });
          return { success: true, hasAccess };
        } catch (error) {
          set({ hasProjectsPlan: false, projectsPlanChecked: true });
          return { success: false, hasAccess: false };
        }
      },

      getAdminUsers: async () => {
        const { sessionToken } = get();
        try {
          const response = await axios.get(`${API}/admin/users`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          return { success: true, users: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to fetch users' };
        }
      },

      deleteUser: async (userId) => {
        const { sessionToken } = get();
        try {
          const response = await axios.delete(`${API}/admin/users/${userId}`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          return { success: true, message: response.data.message };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to delete user' };
        }
      },

      resetUserPassword: async (userId) => {
        const { sessionToken } = get();
        try {
          const response = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to reset password' };
        }
      },

      toggleUserAdmin: async (userId) => {
        const { sessionToken } = get();
        try {
          const response = await axios.put(`${API}/admin/users/${userId}/toggle-admin`, {}, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          return { success: true, data: response.data };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to toggle admin status' };
        }
      },

      deleteOwnAccount: async () => {
        const { sessionToken } = get();
        try {
          await axios.delete(`${API}/auth/delete-account`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true
          });
          // Clear local state after successful deletion
          set({ user: null, sessionToken: null, isAuthenticated: false, isDemo: false, demoExpiresAt: null, subscription: null, subscriptionChecked: false });
          return { success: true };
        } catch (error) {
          return { success: false, error: error.response?.data?.detail || 'Failed to delete account' };
        }
      },
    }),
    {
      name: 'nohalfdays-auth',
      partialize: (state) => ({ sessionToken: state.sessionToken, isDemo: state.isDemo }),
    }
  )
);
